import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { SubscriptionGate } from '@/components/paywall/SubscriptionGate';
import { flashcardGateCopy } from '@/lib/paywall';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { FlashCardDeck } from '@/components/flashcards/FlashCardDeck';
import { allLocalFlashcards, cardAudio, localCards, type LearningCard } from '@/content/mobile';
import { localResources } from '@/content/resources';
import { useCatalogResource } from '@/hooks/useLearnerCatalog';
import { learnerApiJson } from '@/lib/learner-api';
import { isPremiumRequiredError, shouldHidePremiumContent } from '@/lib/premium-gate';
import { enqueueActivity } from '@/lib/progress-queue';
import { getLocalFavorites, setLocalFavorites } from '@/lib/account-sync';
import { useLearnerPreferences } from '@/providers/LearnerPreferences';
import { useLearnerAuth } from '@/providers/LearnerAuth';
import { usePremiumAccess } from '@/hooks/usePremiumAccess';
import { track } from '@/lib/analytics';
import { playAudio } from '@/lib/play-audio';

type CloudCard = {
  title: string;
  translations: Record<string, string>;
  sound_url: string | null;
  sound_children_url: string | null;
  image_url: string | null;
  persistency_key: string;
  card_color: string;
};
type CloudSet = { name: string; requires_subscription: boolean; cards: CloudCard[] };

type FlashcardSearch = { range?: string; card?: string };

export const Route = createFileRoute('/app/flashcards/$slug')({
  component: FlashcardsPage,
  validateSearch: (search: Record<string, unknown>): FlashcardSearch => ({
    range: typeof search.range === 'string' ? search.range : undefined,
    card: typeof search.card === 'string' ? search.card : undefined,
  }),
});

export function FlashcardsPage() {
  const { slug } = Route.useParams();
  const search = Route.useSearch();
  const navigate = useNavigate();
  const preferences = useLearnerPreferences();
  const auth = useLearnerAuth();
  const premium = usePremiumAccess();
  const mode = preferences.dialect === 'moob' ? 'moob' : 'hmonglus';
  const catalogResource = useCatalogResource(
    '/v1/flashcard-sets',
    mode,
    preferences.baseLanguage,
    'Flashcards',
    '/app/flashcards',
    slug,
  );
  const cloud = useQuery({
    queryKey: ['flashcard-set', slug],
    queryFn: () => learnerApiJson<CloudSet>(auth?.getToken ?? null, `/v1/flashcard-sets/${encodeURIComponent(slug)}`),
    retry: false,
    enabled: slug !== 'favorites',
  });
  const local = useMemo(() => {
    if (slug === 'favorites') {
      const keys = new Set(getLocalFavorites());
      return allLocalFlashcards(preferences.dialect, preferences.baseLanguage).filter((card) =>
        keys.has(card.persistencyKey),
      );
    }
    const cards = localCards(slug, preferences.dialect, preferences.baseLanguage);
    if (slug !== 'numbers' || !search.range) return cards;
    const [start, end] = search.range.split('-').map(Number);
    return cards.filter((card) => {
      const value = Number.parseInt(card.title, 10);
      return Number.isFinite(value) && value >= start && value <= end;
    });
  }, [preferences.baseLanguage, preferences.dialect, search.range, slug]);
  const paymentRequired = isPremiumRequiredError(cloud.error);
  const localResource = localResources.find((item) => {
    if (item.slug !== slug) return false;
    if (search.range) return item.href.includes(`range=${search.range}`);
    return item.href === `/app/flashcards/${slug}` || item.href.startsWith(`/app/flashcards/${slug}?`);
  });
  const resource = catalogResource ?? localResource;
  const gated =
    slug !== 'favorites' &&
    shouldHidePremiumContent({
      flaggedPremium: Boolean(
        catalogResource?.premium || localResource?.premium || cloud.data?.requires_subscription,
      ),
      paymentRequired,
      isSubscribed: premium.isSubscribed,
    });
  const rawCards: LearningCard[] = gated
    ? []
    : cloud.data?.cards.map((card) => ({
        title: card.title,
        translation: card.translations[preferences.baseLanguage] || card.translations.en || '',
        translations: card.translations,
        sound: card.sound_url,
        soundChildren: card.sound_children_url,
        image: card.image_url,
        persistencyKey: card.persistency_key,
        cardColor: card.card_color,
      })) ?? local;
  const cards =
    slug === 'numbers' && search.range
      ? rawCards.filter((item) => {
          const [start, end] = search.range!.split('-').map(Number);
          const value = Number.parseInt(item.title, 10);
          return Number.isFinite(value) && value >= start && value <= end;
        })
      : rawCards;
  const [index, setIndex] = useState(() => {
    if (!search.card) return 0;
    return 0;
  });
  const [favorites, setFavorites] = useState(getLocalFavorites);
  const completionRecorded = useRef(false);
  const card = index < cards.length ? cards[index] : undefined;
  const title = resource?.title || cloud.data?.name || 'Flashcards';

  useEffect(() => {
    const needle = search.card?.trim().toLowerCase().replace(/[-_]+/g, ' ');
    const start = needle
      ? Math.max(
          0,
          cards.findIndex((item) => item.title.trim().toLowerCase().replace(/[-_]+/g, ' ') === needle),
        )
      : 0;
    setIndex(Number.isFinite(start) && start >= 0 ? start : 0);
    completionRecorded.current = false;
    track('Flashcard Screen', { collection: slug });
  }, [search.card, slug]);

  useEffect(() => {
    if (!card || gated) return;
    void enqueueActivity('flashcard_view', {
      collection: slug,
      card_title: card.title,
      card_persistency_key: card.persistencyKey,
    });
    track('Flash Card Shown', { collection: slug, title: card.title, subtitle: card.translation });
  }, [card?.persistencyKey, gated, slug]);

  useEffect(() => {
    if (gated || !cards.length || index < cards.length || completionRecorded.current) return;
    completionRecorded.current = true;
    track('Last Flash Card Shown', { collection: slug });
    void enqueueActivity('flashcard_set_completion', { collection: slug });
  }, [cards.length, gated, index, slug]);

  const previewImageUrls = (cloud.data?.cards ?? [])
    .map((card) => card.image_url)
    .filter((url): url is string => Boolean(url));
  const gate = flashcardGateCopy(title);

  if (gated) {
    return (
      <SubscriptionGate
        source={gate.source}
        content={slug}
        message={gate.message}
        heroEmoji={gate.heroEmoji}
        stickerImageUrls={previewImageUrls}
        onDismiss={() => void navigate({ to: '/app/resources' })}
      />
    );
  }
  if (cloud.isLoading && !local.length) return <Status>Loading flashcards…</Status>;
  if (!cards.length) {
    return (
      <Status>
        {slug === 'favorites'
          ? 'Heart words while you study, then they will show up here.'
          : 'This flashcard set is not available yet.'}
      </Status>
    );
  }

  const play = () => {
    if (!card) return;
    const source = cardAudio(card, preferences.voice);
    if (source) void playAudio(source);
    track('Flashcard Audio Press', { collection: slug, card: card.title });
  };
  const toggleFavorite = async () => {
    if (!card) return;
    const active = favorites.includes(card.persistencyKey);
    const next = active ? favorites.filter((key) => key !== card.persistencyKey) : [...favorites, card.persistencyKey];
    setFavorites(next);
    setLocalFavorites(next);
    track('Favorite Button Press', {
      'Persistence Key': card.persistencyKey,
      Favorite: !active,
      'Card Title': card.title,
      'Card Translation': card.translation,
    });
    if (auth?.isSignedIn) {
      await learnerApiJson(auth.getToken, active ? `/v1/user/favorites/${encodeURIComponent(card.persistencyKey)}` : '/v1/user/favorites', {
        method: active ? 'DELETE' : 'POST',
        auth: true,
        ...(active ? {} : { body: JSON.stringify({ persistency_key: card.persistencyKey }) }),
      }).catch(() => undefined);
    }
  };
  const exit = () => {
    void navigate({ to: '/app/resources' });
  };

  return (
    <FlashCardDeck
      cards={cards}
      title={title}
      index={index}
      onIndexChange={setIndex}
      favorites={favorites}
      onToggleFavorite={() => void toggleFavorite()}
      onPlayAudio={play}
      canPlayAudio={Boolean(card && cardAudio(card, preferences.voice))}
      onExit={exit}
    />
  );
}

function Status({ children }: React.PropsWithChildren) {
  return (
    <main className="grid min-h-[75vh] place-items-center p-6 text-center">
      <div>{children}</div>
    </main>
  );
}
